import {
  Badge,
  Button,
  ManageableItemsList,
  Tooltip,
  UntitledLoading,
} from '@payaca/ambrosia';
import { PendingEnaApplicationStatuses } from '@payaca/types/connectionApplication';
import { Address } from '@payaca/types/locationTypes';
import { format as formatDate } from 'date-fns';
import { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { Link, useParams } from 'react-router-dom';
import { useHashFragment } from '../../../../../utils/customHooks';
import ConnectionApplicationChooseInstallationModal from '../ConnectionApplicationChooseInstallationModal';

import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import { ConnectionApplication } from '@/gql/graphql';
import { getConnectionApplicationStatusBadgeColour } from '@/helpers/connectionApplications';

import {
  DateFormats,
  getInternationalDateFormatByLocale,
} from '@payaca/helpers/internationalHelper';
import useGetProjectByIdOverview from '../../../../../api/queries/project/useGetProjectByIdOverview';
import EnaCommissioningStatusBadge from '../../../../components/enaCommissioningStatusBadge/EnaCommissioningStatusBadge';

const willRefresh = (
  application: Pick<
    ConnectionApplication,
    'connectionApplicationStatus' | 'status'
  >
): boolean => {
  const possibleStatuses: readonly string[] = PendingEnaApplicationStatuses;
  return (
    (application.connectionApplicationStatus &&
      possibleStatuses.includes(application.connectionApplicationStatus)) ||
    application.status === 'pending'
  );
};

const DnoApplicationsCard: FC = () => {
  const [showChooseInstallationModal, toggleChooseInstallationModal] =
    useHashFragment('#choose-installation');
  const [shouldRefresh, setShouldRefresh] = useState(false);
  /**
   * Router
   */
  const { projectId } = useParams<{ projectId: string }>();
  const history = useHistory();

  /**
   * Queries
   */
  const { project } = useGetProjectByIdOverview(projectId, shouldRefresh);
  const { data: accountRegionPreferences } = useGetMyRegionPreferences();

  const hasPendingApplication = !!project?.connectionApplications?.find((app) =>
    willRefresh(app)
  );
  useEffect(
    () => setShouldRefresh(hasPendingApplication),
    [hasPendingApplication]
  );

  const pollingForUpdates = hasPendingApplication
    ? 'No need to refresh, we are polling for updates. '
    : '';

  const anApplicationRequiresCommissioningInfoSubmission =
    project?.connectionApplications?.some(
      (application) =>
        application.commissioning && !application.commissioning.submittedAt
    );

  return (
    <>
      <ManageableItemsList>
        <ManageableItemsList.HeaderBar
          heading="DNO applications"
          subHeading={`${pollingForUpdates}If an application needs manual approval from the DNO, status changes may not be visible for up to an hour.`}
          buttons={
            <>
              <Button
                onClick={toggleChooseInstallationModal}
                size={'sm'}
                variant={
                  anApplicationRequiresCommissioningInfoSubmission
                    ? 'white'
                    : 'solid'
                }
              >
                Create application
              </Button>
            </>
          }
        />
        <ManageableItemsList.Table
          uniqueKey="id"
          items={project?.connectionApplications || []}
          onClickRow={(row) =>
            history.push(
              `/projects/${projectId}/compliance/connection-application/${row.id}`
            )
          }
          emptyText="No applications"
        >
          <ManageableItemsList.Table.Column
            header="Type"
            field="data"
            render={(data, row) => {
              return (
                <Link
                  to={`/projects/${projectId}/compliance/connection-application/${row.id}`}
                >
                  {(data?.['lcts'] as string[] | undefined)?.join(', ') || 'HP'}
                </Link>
              );
            }}
          />
          <ManageableItemsList.Table.Column<ConnectionApplication, 'status'>
            header="Status"
            field="status"
            render={(status, row) => {
              const enaStatus = row.connectionApplicationStatus;
              return (
                <Badge
                  colour={getConnectionApplicationStatusBadgeColour(status)}
                  variant="soft"
                  className="capitalize"
                >
                  <SelfUpdatingWrapper isRefreshing={willRefresh(row)}>
                    {status}
                    {enaStatus ? ` (${enaStatus})` : ''}
                  </SelfUpdatingWrapper>
                </Badge>
              );
            }}
          />

          <ManageableItemsList.Table.Column<
            ConnectionApplication,
            'commissioning'
          >
            header="Commissioning"
            field="commissioning"
            render={(commissioning, row) => {
              if (commissioning) {
                const commissioningUrl = `/projects/${projectId}/compliance/connection-application/${row.id}/commissioning`;

                // Show status progression: Submit → Submitted → Status Badge
                if (commissioning.submittedAt) {
                  return (
                    <Link
                      to={commissioningUrl}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <EnaCommissioningStatusBadge
                        commissioningStatus={commissioning.commissioningStatus}
                      />
                    </Link>
                  );
                } else {
                  return (
                    <Button
                      variant="solid"
                      size="sm"
                      onClick={(event) => {
                        history.push(commissioningUrl);
                        event.stopPropagation();
                      }}
                    >
                      Submit
                    </Button>
                  );
                }
              }
            }}
          />
          <ManageableItemsList.Table.Column
            header="Address"
            field="data"
            render={(data) => {
              const address = data?.['installerCustomerDetails']?.[
                'mainAddress'
              ] as
                | Pick<
                    Address,
                    'line1' | 'line2' | 'city' | 'country' | 'postcode'
                  >
                | undefined;

              if (!address) {
                return '---';
              }

              const fullAddress = [
                address.line1,
                address.line2,
                address.city,
                address.country,
                address.postcode,
              ]
                .filter((i) => !!i)
                .join(', ');

              return (
                <Tooltip
                  tooltipContent={fullAddress}
                  className="max-w-[13vw] overflow-hidden text-ellipsis whitespace-nowrap"
                >
                  {fullAddress}
                </Tooltip>
              );
            }}
          />
          <ManageableItemsList.Table.Column
            header="Created"
            field="createdAt"
            render={(createdAt) => {
              return (
                <Tooltip tooltipContent={new Date(createdAt).toLocaleString()}>
                  {formatDate(
                    createdAt,
                    getInternationalDateFormatByLocale(
                      DateFormats.SHORT,
                      accountRegionPreferences?.locale
                    )
                  )}
                </Tooltip>
              );
            }}
          />
          <ManageableItemsList.Table.Column
            header="Created by"
            field="createdBy"
            render={(createdBy) => createdBy?.fullName || ''}
          />
          <ManageableItemsList.Table.Column
            header="Submitted"
            field="submittedAt"
            render={(submittedAt) => {
              if (!submittedAt) return '';

              return (
                <Tooltip
                  tooltipContent={new Date(submittedAt).toLocaleString()}
                >
                  {formatDate(
                    submittedAt,
                    getInternationalDateFormatByLocale(
                      DateFormats.SHORT,
                      accountRegionPreferences?.locale
                    )
                  )}
                </Tooltip>
              );
            }}
          />
          <ManageableItemsList.Table.Column
            header="Submitted by"
            field="submittedBy"
            render={(submittedBy) => submittedBy?.fullName || ''}
          />
        </ManageableItemsList.Table>
      </ManageableItemsList>
      <ConnectionApplicationChooseInstallationModal
        isOpen={showChooseInstallationModal}
        onClose={toggleChooseInstallationModal}
      />
    </>
  );
};

const SelfUpdatingWrapper: FC<{
  isRefreshing: boolean;
  children: React.ReactNode;
}> = ({ isRefreshing, children }) => {
  if (!isRefreshing) return children;
  return (
    <Tooltip
      tooltipContent="Monitoring for updates..."
      className="flex items-center"
    >
      <UntitledLoading className="mr-2 w-4" />
      {children}
    </Tooltip>
  );
};

export default DnoApplicationsCard;
